import * as Sentry from '@sentry/browser';
import { AppError } from '@or-sdk/hitl';

/**
 * Some variables like "App Version" can be hardcoded during build.
 * Some variables only hardcode backup values so they can work in ActionDesk.
 * App Launcher should provide configuration using OR Config.
 */
export class SentryAPI {
  public static MAX_BREADCRUMBS = 50;
  public static SAMPLE_RATE = 1;
  public static TRACES_SAMPLE_RATE = 0.01;
  public static BREADCRUMBS_IGNORE = ['vue-i18n'];

  private static _debugLogs = false;

  public static isReady() {
    return Sentry.isInitialized();
  }

  public static makeInstance(args: {
    dsn?: string;
    /** Sentry specific value used to group events */
    environment?: string;
    /** Simply provided as a tag */
    env: string;
    client: string;
    userId: string;
    accountId: string;
    username: string;
    sampleRate?: number;
    tracesSampleRate?: number;
    debugLogs?: boolean;
  }) {
    if (!args.dsn && !HITL_SENTRY_DSN) {
      return;
    }

    Sentry.init({
      dsn: args.dsn || HITL_SENTRY_DSN,
      release: HITL_PKG_VERSION,
      environment: args.environment || HITL_SENTRY_ENV,
      maxBreadcrumbs: this.MAX_BREADCRUMBS,
      autoSessionTracking: true,
      sampleRate: 1.0, // args.sampleRate || this.SAMPLE_RATE,
      tracesSampleRate: 1.0, // || args.tracesSampleRate || this.TRACES_SAMPLE_RATE,
      integrations: Sentry.getDefaultIntegrations({}),
      transport: Sentry.makeFetchTransport,
      stackParser: Sentry.defaultStackParser,
      /** Ignore some events that are not relevant to potential error */
      beforeBreadcrumb: (breadcrumb => {
        for (const term of this.BREADCRUMBS_IGNORE) {
          if (breadcrumb.message?.includes(term)) {
            return null;
          }
        }
        return breadcrumb;
      }),
    });

    const scope = Sentry.getIsolationScope();
    scope.setTags({
      client: args.client,
      env: args.env,
      accountId: args.accountId,
    });
    scope.setUser({ id: args.userId, email: args.username });

    this._debugLogs = Boolean(args.debugLogs);
  }

  public static captureMessage = (message: string, data?: Record<string, any>) => {
    if (HITL_ENABLE_SENTRY && this._debugLogs) {
      Sentry.captureMessage(message, {
        level: 'debug',
        extra: data || {},
      });
    }
  };

  /** Bind method to use as a callback */
  public static captureException = (e: Error, data?: Record<string, string>) => {
    if (HITL_ENABLE_SENTRY) {
      Sentry.captureException(e, { extra: data });
    }
    console.error(e.stack);
  };

  public static captureAPIError = (e: Error | AppError) => {
    if (e instanceof AppError) {
      const data = {
        APIErrorDetails: JSON.stringify(e.detailsToObject()),
      };

      SentryAPI.captureException(e, data);
    } else {
      SentryAPI.captureException(e);
    }
  };
}
